import { DataInputSectionConfiguration } from "./configurationTypes";
import {
  getSubPopulationFeatureID,
  KahunCategories,
  KahunPanels,
  KahunPanelSubGroup,
  Relations,
} from "shared";
import {
  Feature,
  QualifiedFeature,
  QualifiedFeatureFragment,
  Qualifier,
} from "fe-common/graphql";
import { PatientCaseGrouping } from "../../../context/diagnosticAgent";

const FAMILY_HISTORY_FEATURE = getSubPopulationFeatureID(
  Relations.FAMILY_HISTORY,
);
export const EXPOSURE_TO_FEATURE = getSubPopulationFeatureID(
  Relations.EXPOSURE_TO,
);

const anyCategories = () => [];

const demographicsSearchFilter = (context) => {
  switch (context?.panel?.id) {
    case KahunPanels.Ocupation:
      return [KahunCategories.OCCUPATION];

    case KahunPanels.GeographicLocation:
      return [KahunCategories.GEOGRAPHICAL_LOCATION];

    case KahunPanels.EthnicGroup:
      return [KahunCategories.ETHNIC_GROUP];

    case KahunPanels.TravelHistory:
      return [KahunCategories.GEOGRAPHICAL_LOCATION];

    case KahunPanels.ExposureTo:
      return [];
  }
  return [];
};

const transformDemographics = (qf, context) => {
  const relationByPanel = {
    [KahunPanels.Ocupation]: [Relations.OCCUPATION, "Occupation"],
    [KahunPanels.GeographicLocation]: [
      Relations.LOCATION,
      "Geographic Location",
    ],
    [KahunPanels.EthnicGroup]: [Relations.ETHNIC_GROUP, "Ethnic Group"],
    [KahunPanels.TravelHistory]: [Relations.LOCATION, "Travel History"],
    [KahunPanels.ExposureTo]: [Relations.EXPOSURE_TO, "Exposure"],
  };

  const relation = relationByPanel[context?.panel?.id];

  if (!relation || !qf || !qf.feature) return qf;

  return makeSubPopulationQualifiedFeature(
    relation[0],
    relation[1],
    qf.feature,
  );
};

const transformProcedures = (qf, context) => {
  if (!qf || !qf.feature) return qf;

  return makeProcedureQualifiedFeature(
    Relations.PROCEDURE_FINDING,
    "Procedure Finding",
    qf.feature,
  );
};

export const DataInputSectionConfigurations: Partial<Record<
  KahunPanelSubGroup,
  DataInputSectionConfiguration
>> = {
  [KahunPanelSubGroup.SignSymptom]: {
    id: KahunPanelSubGroup.SignSymptom,
    defaultForCategories: [
      KahunCategories.SIGNS_SYMPTOMS,
      KahunCategories.SIGN,
      KahunCategories.KAHUN_SYMPTOMS,
    ],
    showSearch: true,
    displayName: "Signs",
    displayOrder: 2,
    defaultSections: [
      PatientCaseGrouping.CLINICAL_FEATURES,
      PatientCaseGrouping.MAIN,
    ],
    hasMyPatient: true,
    allowYesNo: true,
    allowRefinement: true,
  },

  [KahunPanelSubGroup.Symptoms]: {
    id: KahunPanelSubGroup.Symptoms,
    defaultForCategories: [
      KahunCategories.SIGNS_SYMPTOMS,
      KahunCategories.SIGN,
      KahunCategories.KAHUN_SYMPTOMS,
    ],
    showSearch: true,
    displayName: "Symptoms",
    displayOrder: 1,
    defaultSections: [PatientCaseGrouping.REVIEW_OF_SYSTEMS],
    hasMyPatient: true,
    allowYesNo: true,
    allowRefinement: true,
  },

  [KahunPanelSubGroup.ActiveProblems]: {
    id: KahunPanelSubGroup.ActiveProblems,
    defaultForCategories: [KahunCategories.DISORDERS],
    showSearch: true,
    displayName: "Active Conditions",
    displayOrder: 3,
    allowYesNo: true,
    defaultSections: [PatientCaseGrouping.CLINICAL_FEATURES],
    hasMyPatient: true,
  },
  [KahunPanelSubGroup.Habits]: {
    id: KahunPanelSubGroup.Habits,
    defaultForCategories: [
      KahunCategories.HABITS,
      KahunCategories.RISK_FACTORS,
    ],
    showSearch: true,
    displayName: "Risk Factors",
    displayOrder: 4,
    allowYesNo: true,
    defaultSections: [PatientCaseGrouping.BACKGROUND],
  },
  [KahunPanelSubGroup.MedicalHistory]: {
    id: KahunPanelSubGroup.MedicalHistory,
    defaultForCategories: [KahunCategories.DISORDERS],
    showSearch: true,
    displayOrder: 1,
    displayName: "Medical History",
    allowYesNo: true,
    defaultSections: [PatientCaseGrouping.BACKGROUND],
  },
  [KahunPanelSubGroup.ALLERGIES]: {
    id: KahunPanelSubGroup.ALLERGIES,
    defaultForCategories: [KahunCategories.ALLERGIES],
    showSearch: true,
    displayOrder: 5,
    displayName: "Allergies",
    allowYesNo: true,
    defaultSections: [PatientCaseGrouping.BACKGROUND],
  },
  [KahunPanelSubGroup.VitalSign]: {
    id: KahunPanelSubGroup.VitalSign,
    defaultForCategories: [KahunCategories.VITAL_SIGNS],
    showSearch: true,
    displayName: "Vital Signs",
    displayOrder: 1,
    defaultSections: [PatientCaseGrouping.CLINICAL_FEATURES],
  },
  [KahunPanelSubGroup.Labs]: {
    id: KahunPanelSubGroup.Labs,
    defaultForCategories: [KahunCategories.LAB_TEST],
    showSearch: true,
    displayName: "Labs",
    displayOrder: 7,
    defaultSections: [PatientCaseGrouping.WORKUP],
    hasMyPatient: true,
    allowYesNo: true,
  },
  [KahunPanelSubGroup.Procedures]: {
    id: KahunPanelSubGroup.Procedures,
    defaultForCategories: [KahunCategories.IMAGING, KahunCategories.PROCEDURE],
    showSearch: true,
    searchCategories: () => [KahunCategories.PROCEDURE],
    displayName: "Procedures",
    displayOrder: 8,
    defaultSections: [PatientCaseGrouping.WORKUP],
    hasMyPatient: true,
    allowYesNo: false,
    transformResult: transformProcedures,
  },
  [KahunPanelSubGroup.FamliyHistory]: {
    id: KahunPanelSubGroup.FamliyHistory,
    defaultForFeatures: [FAMILY_HISTORY_FEATURE],
    searchCategories: () => [KahunCategories.DISORDERS],
    showSearch: true,
    transformResult: (qf) =>
      makeSubPopulationQualifiedFeature(
        Relations.FAMILY_HISTORY,
        "Family History",
        qf.feature,
      ),
    displayName: "Family History",
    allowYesNo: true,
    displayOrder: 3,
    defaultSections: [PatientCaseGrouping.BACKGROUND],
  },
  [KahunPanelSubGroup.Medication]: {
    id: KahunPanelSubGroup.Medication,
    defaultForFeatures: [EXPOSURE_TO_FEATURE],
    defaultForCategories: [KahunCategories.MEDICINE],
    searchCategories: () => [
      KahunCategories.MEDICINE,
      KahunCategories.TREATMENT,
    ],
    showSearch: true,
    transformResult: (qf) =>
      makeSubPopulationQualifiedFeature(
        Relations.EXPOSURE_TO,
        "Exposure",
        qf.feature,
      ),
    displayName: "Medication",
    allowYesNo: false,
    displayOrder: 2,
    defaultSections: [PatientCaseGrouping.BACKGROUND],
  },
  [KahunPanelSubGroup.RecentExposure]: {
    id: KahunPanelSubGroup.RecentExposure,
    searchCategories: anyCategories,
    showSearch: false,
    displayName: "Recent Exposure",
    allowYesNo: true,
    displayOrder: 6,
    defaultSections: [PatientCaseGrouping.BACKGROUND],
  },
  [KahunPanelSubGroup.Demographics]: {
    id: KahunPanelSubGroup.Demographics,
    searchCategories: demographicsSearchFilter,
    defaultForCategories: [KahunCategories.DEMOGRAPHIC],
    showSearch: true,
    displayName: "Demographics",
    displayOrder: 7,
    defaultSections: [PatientCaseGrouping.BACKGROUND],
    transformResult: transformDemographics,
    searchSubCategories: true,
  },
  //In case a feature is not mapped to any group, put it in other
  [KahunPanelSubGroup.Other]: {
    searchCategories: anyCategories,
    id: KahunPanelSubGroup.Other,
    displayName: "Other",
    displayOrder: 1,
    defaultSections: [
      PatientCaseGrouping.CLINICAL_FEATURES,
      PatientCaseGrouping.BACKGROUND,
      PatientCaseGrouping.WORKUP,
      PatientCaseGrouping.MAIN,
    ],
  },
};

export const isCompatibleDataInputSection = (
  qf: QualifiedFeatureFragment,
  section: PatientCaseGrouping,
  disc: DataInputSectionConfiguration,
) => {
  return (
    disc.defaultSections.includes(section) &&
    ((disc.defaultForFeatures &&
      disc.defaultForFeatures.includes(qf.feature.id)) ||
      (disc.defaultForCategories &&
        disc.defaultForCategories.includes(
          qf.feature.category?.id as KahunCategories,
        )))
  );
};

export const getContainedSubGroup = (
  qf: QualifiedFeatureFragment,
  section: PatientCaseGrouping,
): DataInputSectionConfiguration => {
  const subgroup = Object.values(DataInputSectionConfigurations).find((disc) =>
    isCompatibleDataInputSection(qf, section, disc),
  );
  return subgroup || DataInputSectionConfigurations[KahunPanelSubGroup.Other];
};

// TBD move somewhere more general (JM)
export function makeSubPopulationQualifiedFeature(
  relationId: Relations,
  relationName: string,
  value: Feature,
): QualifiedFeature {
  const feature: Partial<Feature> = {
    name: relationName,
    id: getSubPopulationFeatureID(relationId),
    category: {
      id: KahunCategories.DEMOGRAPHIC,
      name: "Demographic",
      shortname: "Demographic",
    },
  };
  const qualifier: Partial<Qualifier> = {
    relation: {
      name: relationName,
      id: relationId,
      type: null,
      multi: false,
      essence: null,
    },
    value: {
      ...value,
      parents: {
        features: [],
      },
      range: null,
    },
  };
  return {
    id: `${relationId}_${value.id}`,
    feature,
    qualifiers: [qualifier],
    mandatory: qualifier,
  } as any;
}

// TBD move somewhere more general (JM)
export function makeProcedureQualifiedFeature(
  relationId: Relations,
  relationName: string,
  value: Feature,
): QualifiedFeature {
  const feature: Partial<Feature> = {
    name: relationName,
    id: getSubPopulationFeatureID(relationId),
    category: {
      id: KahunCategories.IMAGING,
      name: "Imaging",
      shortname: "Imaging",
    },
  };
  const qualifier: Partial<Qualifier> = {
    relation: {
      name: relationName,
      id: relationId,
      type: null,
      multi: false,
      essence: null,
    },
    value: {
      ...value,
      parents: {
        features: [],
      },
      range: null,
    },
  };
  return {
    id: `${relationId}_${value.id}`,
    feature,
    qualifiers: [qualifier],
    mandatory: qualifier,
  } as any;
}
